import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import deLocale from 'date-fns/locale/de';
import theme from './theme';
import { AuthProvider } from './auth/AuthContext';
import { ScrollToTop } from './components/ScrollToTop';

// Öffentliche Seiten
import Layout from './components/Layout/Layout';
import Home from './pages/Home';
import Gerichte from './pages/Gerichte';
import Entscheidungen from './pages/Entscheidungen';
import EntscheidungDetail from './pages/EntscheidungDetail';
import Impressum from './pages/Impressum';
import GerichtDetail from './pages/GerichtDetail';
import Mitteilungen from './pages/Mitteilungen';
import MitteilungDetail from './pages/MitteilungDetail';
import Streitwertkatalog from './pages/Streitwertkatalog';
import Barrierefreiheit from './pages/Barrierefreiheit';
import Kontakt from './pages/Kontakt';
import Berufskunde from './pages/Berufskunde';
import BerufskundeDetail from './pages/BerufskundeDetail';
import TempRegistration from './pages/TempRegistration';

// Admin-Seiten
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import GerichteList from './pages/admin/GerichteList';
import EntscheidungenList from './pages/admin/EntscheidungenList';
import EntscheidungAdd from './pages/admin/EntscheidungAdd';
import EntscheidungEdit from './pages/admin/EntscheidungEdit';
import UserList from './pages/admin/UserList';
import RoleList from './pages/admin/RoleList';
import { PrivateRoute } from './components/PrivateRoute';
import PasswordChange from './pages/admin/PasswordChange';
import MitteilungenList from './pages/admin/MitteilungenList';
import MitteilungAdd from './pages/admin/MitteilungAdd';
import MitteilungEdit from './pages/admin/MitteilungEdit';
import BerufskundeList from './pages/admin/BerufskundeList';
import BerufskundeAdd from './pages/admin/BerufskundeAdd';
import BerufskundeEdit from './pages/admin/BerufskundeEdit';
import Indexierung from './pages/admin/Indexierung';
import AdminLayout from './components/Layout/AdminLayout';

function App() {
    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
                <CssBaseline />
                <AuthProvider>
                    <Router>
                        <ScrollToTop />
                        <Routes>
                            {/* Öffentliche Routen */}
                            <Route path="/" element={<Layout><Home /></Layout>} />
                            <Route path="/register" element={<Layout><TempRegistration /></Layout>} />
                            <Route path="/gerichte" element={<Layout><Gerichte /></Layout>} />
                            <Route path="/gerichte/:id" element={<Layout><GerichtDetail /></Layout>} />
                            <Route path="/entscheidungen" element={<Layout><Entscheidungen /></Layout>} />
                            <Route path="/entscheidungen/:id" element={<Layout><EntscheidungDetail /></Layout>} />
                            <Route path="/mitteilungen" element={<Layout><Mitteilungen /></Layout>} />
                            <Route path="/mitteilungen/:id" element={<Layout><MitteilungDetail /></Layout>} />
                            <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
                            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                            <Route path="/streitwertkatalog" element={<Layout><Streitwertkatalog /></Layout>} />
                            <Route path="/barrierefreiheit" element={<Layout><Barrierefreiheit /></Layout>} />
                            <Route path="/kontakt" element={<Layout><Kontakt /></Layout>} />
                            <Route path="/berufskunde" element={<Layout><Berufskunde /></Layout>} />
                            <Route path="/berufskunde/:id" element={<Layout><BerufskundeDetail /></Layout>} />

                            {/* Admin-Routen */}
                            <Route path="/admin" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <AdminPage />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/gerichte" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <GerichteList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/entscheidungen" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <EntscheidungenList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/entscheidungen/add" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <EntscheidungAdd />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/entscheidungen/edit/:id" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <EntscheidungEdit />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/users" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <UserList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/landmaster/users" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <UserList isLandmasterView={true} />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/roles" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <RoleList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/password-change" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <PasswordChange />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/mitteilungen" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <MitteilungenList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/mitteilungen/add" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <MitteilungAdd />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/mitteilungen/:id/edit" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <MitteilungEdit />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />

                            {/* Berufskunde Routen */}
                            <Route path="/admin/berufskunde" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <BerufskundeList />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/berufskunde/add" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <BerufskundeAdd />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/berufskunde/:id/edit" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <BerufskundeEdit />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />
                            <Route path="/admin/indexierung" element={
                                <PrivateRoute>
                                    <AdminLayout>
                                        <Indexierung />
                                    </AdminLayout>
                                </PrivateRoute>
                            } />

                            {/* Fallback */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Router>
                </AuthProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
}

export default App; 